/**
 * Language Manager - Manages game localization and translations
 */

import { LANGUAGES, DEFAULT_LANGUAGE, AVAILABLE_LANGUAGES } from '../config/languages.js';
import { GAME_CONFIG } from '../config/gameConfig.js';

export class LanguageManager {
    constructor() {
        this.currentLanguage = DEFAULT_LANGUAGE;
        this.languages = LANGUAGES;
        this.availableLanguages = AVAILABLE_LANGUAGES;

        // Load language preference (priority: localStorage > gameConfig > default)
        this.loadLanguagePreference();
    }

    /**
     * Load language preference from localStorage or gameConfig
     */
    loadLanguagePreference() {
        try {
            // Priority 1: Check localStorage (user preference)
            const saved = localStorage.getItem('gameLanguage');
            if (saved && this.languages[saved]) {
                this.currentLanguage = saved;
                console.log(`🌐 Loaded saved language from localStorage: ${saved}`);
                return;
            }

            // Priority 2: Check gameConfig.js
            if (GAME_CONFIG.language && this.languages[GAME_CONFIG.language]) {
                this.currentLanguage = GAME_CONFIG.language;
                console.log(`🌐 Loaded language from gameConfig.js: ${GAME_CONFIG.language}`);
                return;
            }

            // Priority 3: Use default
            console.log(`🌐 Using default language: ${DEFAULT_LANGUAGE}`);
        } catch (error) {
            console.warn('Failed to load language preference:', error);
        }
    }

    /**
     * Set current language
     * @param {string} langCode - Language code (e.g., 'en', 'zh')
     */
    setLanguage(langCode) {
        if (!this.languages[langCode]) {
            console.warn(`Language '${langCode}' not found, using default`);
            langCode = DEFAULT_LANGUAGE;
        }

        this.currentLanguage = langCode;

        // Save to localStorage
        try {
            localStorage.setItem('gameLanguage', langCode);
            console.log(`🌐 Language set to: ${langCode}`);
        } catch (error) {
            console.warn('Failed to save language preference:', error);
        }

        // Trigger language change event for UI updates
        this.notifyLanguageChange();
    }

    /**
     * Get current language code
     * @returns {string}
     */
    getCurrentLanguage() {
        return this.currentLanguage;
    }

    /**
     * Get translated text
     * @param {string} key - Translation key (e.g., 'menu.startButton')
     * @param {Object} params - Parameters for template replacement
     * @returns {string}
     */
    t(key, params = {}) {
        const keys = key.split('.');
        let value = this.languages[this.currentLanguage];

        // Navigate through nested keys
        for (const k of keys) {
            if (value && typeof value === 'object') {
                value = value[k];
            } else {
                console.warn(`Translation key not found: ${key}`);
                return key; // Return key if translation not found
            }
        }

        // If value is still an object, something went wrong
        if (typeof value === 'object') {
            console.warn(`Translation key is incomplete: ${key}`);
            return key;
        }

        // Replace template parameters
        return this.replaceParams(value, params);
    }

    /**
     * Replace template parameters in translated string
     * @param {string} text - Text with {{param}} placeholders
     * @param {Object} params - Parameters to replace
     * @returns {string}
     */
    replaceParams(text, params) {
        if (typeof text !== 'string') {
            return text;
        }

        return text.replace(/\{\{(\w+)\}\}/g, (match, param) => {
            return params[param] !== undefined ? params[param] : match;
        });
    }

    /**
     * Get all available languages
     * @returns {Array}
     */
    getAvailableLanguages() {
        return this.availableLanguages;
    }

    /**
     * Notify UI components of language change
     */
    notifyLanguageChange() {
        // Dispatch custom event for language change
        const event = new CustomEvent('languageChanged', {
            detail: { language: this.currentLanguage }
        });
        window.dispatchEvent(event);
    }

    /**
     * Update all DOM elements with translation attributes
     */
    updateDOM() {
        // Update elements with data-i18n attribute
        const elements = document.querySelectorAll('[data-i18n]');
        elements.forEach(element => {
            const key = element.getAttribute('data-i18n');
            const params = this.getElementParams(element);
            element.textContent = this.t(key, params);
        });

        // Update elements with data-i18n-placeholder attribute
        const placeholderElements = document.querySelectorAll('[data-i18n-placeholder]');
        placeholderElements.forEach(element => {
            const key = element.getAttribute('data-i18n-placeholder');
            const params = this.getElementParams(element);
            element.placeholder = this.t(key, params);
        });
    }

    /**
     * Get parameters from element's data attributes
     * @param {HTMLElement} element
     * @returns {Object}
     */
    getElementParams(element) {
        const params = {};
        const dataAttrs = element.dataset;

        for (const key in dataAttrs) {
            if (key.startsWith('i18nParam')) {
                const paramName = key.replace('i18nParam', '').toLowerCase();
                params[paramName] = dataAttrs[key];
            }
        }

        return params;
    }

    /**
     * Check if language is Chinese
     * @returns {boolean}
     */
    isChinese() {
        return this.currentLanguage === 'zh';
    }

    /**
     * Check if language is English
     * @returns {boolean}
     */
    isEnglish() {
        return this.currentLanguage === 'en';
    }
}
